import React from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Badge from '../../../components/ui/Badge';

const UserTable = ({ users, onEdit, onDelete, onAllocateCenters, selectedUsers, onSelectUser, onSelectAll, currentUserRole }) => {
  const getRoleBadgeVariant = (role) => {
    switch (role) {
      case 'super_admin':
        return 'purple';
      case 'admin':
        return 'blue';
      case 'center_manager':
        return 'orange';
      case 'center_manager_ops':
        return 'teal';
      case 'sales':
        return 'green';
      default:
        return 'gray';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'super_admin':
        return 'Super Admin';
      case 'admin':
        return 'Admin/Owner';
      case 'center_manager':
        return 'Center Manager Sales';
      case 'center_manager_ops':
        return 'Center Manager Ops';
      case 'sales':
        return 'Sales';
      default:
        return role;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const allSelected = users?.length > 0 && selectedUsers?.length === users?.length;

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={(e) => onSelectAll(e?.target?.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                User
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Role
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Hierarchy
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Centers
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Phone
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Created
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users?.map((user) => (
              <tr key={user?.id} className="hover:bg-gray-50">
                <td className="px-4 py-4">
                  <input
                    type="checkbox"
                    checked={selectedUsers?.includes(user?.id)}
                    onChange={(e) => onSelectUser(user?.id, e?.target?.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10">
                      {user?.avatarUrl ? (
                        <img
                          src={user?.avatarUrl}
                          alt={user?.fullName}
                          className="h-10 w-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <span className="text-sm font-medium text-blue-600">
                            {user?.fullName?.charAt(0)?.toUpperCase()}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">{user?.fullName}</div>
                      <div className="text-sm text-gray-500">{user?.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <Badge variant={getRoleBadgeVariant(user?.role)}>
                    {getRoleLabel(user?.role)}
                  </Badge>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  {user?.parentAdmin ? (
                    <div className="text-xs">
                      <div className="text-gray-500">Reports to:</div>
                      <div className="text-gray-900 font-medium">{user?.parentAdmin?.fullName}</div>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400">—</span>
                  )}
                </td>
                <td className="px-4 py-4">
                  {user?.role === 'admin' && user?.adminCenterAllocations?.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {user?.adminCenterAllocations?.slice(0, 2)?.map((alloc) => (
                        <Badge key={alloc?.centerId} variant="gray" className="text-xs">
                          {alloc?.centers?.name || alloc?.centerId}
                        </Badge>
                      ))}
                      {user?.adminCenterAllocations?.length > 2 && (
                        <Badge variant="gray" className="text-xs">
                          +{user?.adminCenterAllocations?.length - 2}
                        </Badge>
                      )}
                    </div>
                  ) : user?.centerId ? (
                    <div className="text-sm text-gray-900">{user?.centerId}</div>
                  ) : (
                    <span className="text-sm text-gray-400">—</span>
                  )}
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{user?.phone || '—'}</div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  {user?.isActive ? (
                    <Badge variant="success">Active</Badge>
                  ) : (
                    <Badge variant="danger">Inactive</Badge>
                  )}
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{formatDate(user?.createdAt)}</div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex space-x-2">
                    <Button variant="ghost" size="sm" onClick={() => onEdit(user)} iconName="Edit" />
                    {currentUserRole === 'super_admin' && user?.role === 'admin' && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => onAllocateCenters(user)} 
                        iconName="Building"
                        className="text-blue-600 hover:text-blue-700"
                      />
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDelete(user?.id)}
                      iconName="Trash2"
                      className="text-red-600 hover:text-red-700"
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {users?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="Users" size={48} className="mx-auto mb-4 text-gray-400" />
          <p className="text-gray-600">No users found</p>
        </div>
      )}
    </div>
  );
};

export default UserTable;